// Draft-flow API wrapper for the Crude Registration Create wizard.
// Implements the "Alur Baru: Draft Bertahap" from
// .claude/formview-reference/crude-registration/api-crude-registration.md (§D1–D6):
//   Tab 1 Next   → createDraft / updateDraft
//   Tab 2 & 3    → uploadDraftDocument / deleteDraftDocument (per file)
//   Tab 3 Submit → submitDraft
//
// All Crude endpoints are on the crude host (crudeApi), including the Evaluation
// Document and CDU master lists that populate Tab 2/3 — see registrationConstants.js.
// The client unwraps `.data` and rejects errors as a plain string message
// (crude_api_helper.js), so callers do `toast.error(\`...: ${error}\`)`.
import { crudeApi, crudeAxios } from '../../../../helpers/crude_api_helper';
import {
  EVALUATION_DOCUMENT_LIST,
  CDU_LIST,
  LOOKUP_PAGE_SIZE,
} from './registrationConstants';

const DRAFT = '/v1/Crude/Draft';
const DRAFT_DOCUMENTS = '/v1/Crude/Draft/Documents';
const DRAFT_SUBMIT = '/v1/Crude/Draft/Submit';

const LIST = '/v1/Crude/GetListCrude';
const GET_CRUDE = '/v1/Crude/GetCrude';
const ADD_DETAIL = '/v1/Crude/AddDetail';
const UPDATE_DETAIL = '/v1/Crude/UpdateDetail';
const DOWNLOAD = '/v1/Crude/Download';

// Detail-scoped Draft flows (§AA1–AA6 / §UA1–UA6) — Add/Update Assay against an
// EXISTING crude, staged per tab like the Create wizard's Draft flow. Documents
// reuse the same /v1/Crude/Draft/Documents endpoint (uploadDraftDocument /
// deleteDraftDocument), keyed by the crudeDetailId these endpoints return.
const ADD_DETAIL_DRAFT = '/v1/Crude/AddDetail/Draft';
const ADD_DETAIL_DRAFT_SUBMIT = '/v1/Crude/AddDetail/Draft/Submit';
const UPDATE_DETAIL_DRAFT = '/v1/Crude/UpdateDetail/Draft';
const UPDATE_DETAIL_DRAFT_SUBMIT = '/v1/Crude/UpdateDetail/Draft/Submit';

// D1 — POST /v1/Crude/Draft (JSON). Returns { crudeId, crudeDetailId }.
export const createDraft = payload => crudeApi.create(DRAFT, payload);

// D2 — PUT /v1/Crude/Draft (JSON, includes crudeId + crudeDetailId).
export const updateDraft = payload => crudeApi.put(DRAFT, payload);

// D4 — POST /v1/Crude/Draft/Documents (multipart, 1 file per call).
// Returns { pendingDocumentId }. Field-name casing must match the [FromForm]
// binder; effDate is an ISO string.
export const uploadDraftDocument = ({
  file,
  crudeDetailId,
  evaluationId,
  refDocument,
  effDate,
}) =>
  crudeApi.postMultipart(DRAFT_DOCUMENTS, {
    file,
    crudeDetailId,
    evaluationId,
    refDocument,
    effDate,
  });

// D5 — DELETE /v1/Crude/Draft/Documents/{pendingDocumentId}.
export const deleteDraftDocument = pendingDocumentId =>
  crudeApi.delete(`${DRAFT_DOCUMENTS}/${pendingDocumentId}`);

// D6 — POST /v1/Crude/Draft/Submit (JSON). Bundles the draft into a
// ChangedRequest (AwaitingReview). Returns { crudeId }.
export const submitDraft = payload => crudeApi.create(DRAFT_SUBMIT, payload);

// Master lookups (crudeApi). Return the raw `items` array (empty on failure
// is left to the caller's try/catch).
//
// `evaluationType` (§D4): Tab 2 omits it (BE default = Evaluation), Tab 3 passes
// EVALUATION_TYPE.IMPLEMENTATION to get the 5 fixed attachment rows instead of
// the Evaluation checklist. Blank/undefined is dropped by the query builder.
export const fetchEvaluationDocuments = evaluationType =>
  crudeApi
    .get(EVALUATION_DOCUMENT_LIST, {
      page: 1,
      pageSize: LOOKUP_PAGE_SIZE,
      evaluationType: evaluationType || undefined,
    })
    .then(res => (Array.isArray(res?.items) ? res.items : []));

export const fetchCduList = () =>
  crudeApi
    .get(CDU_LIST, { page: 1, pageSize: LOOKUP_PAGE_SIZE })
    .then(res => (Array.isArray(res?.items) ? res.items : []));

// --- Detail page (api-crude-registration.md §1/§3/§4/§5) --------------------

// §1 — GET /v1/Crude/GetListCrude, used by the Detail page's Crude selector
// (AsyncSelect). Returns the raw `items` array of GetListCrudeResponse
// ({ id, crudeName, origin, country, codeG5 }).
export const getCrudeList = ({ searchText, page = 1, pageSize = 25 } = {}) =>
  crudeApi
    .get(LIST, { page, pageSize, searchText: searchText || undefined })
    .then(res => (Array.isArray(res?.items) ? res.items : []));

// §3 — GET /v1/Crude/GetCrude/{id}. Returns the full GetCrudeResponse (header +
// details[] + compositions[] + crudeNavigation + dataStatus).
// ⚠️ A not-found id returns 500 (unhandled NullReferenceException), NOT 404 —
// callers should treat ANY rejection here as "crude not found".
export const getCrude = id => crudeApi.get(`${GET_CRUDE}/${id}`);

// Build the AddDetail/UpdateDetail multipart body: one `payload` JSON text part +
// N `documents` file parts. The server matches each file to a payload.documents[]
// entry by the multipart FILENAME (3rd append arg), which must be exactly
// `"{evaluationId}_{fileName}"` — so we use FormData.append(field, file, partName)
// (the generic `postMultipart`/`buildFormData` can't set a custom filename or
// append multiple parts under one field, hence the raw path).
const buildAssayFormData = ({ payload, files }) => {
  const fd = new FormData();
  fd.append('payload', payload);
  (files || []).forEach(({ file, partName }) => {
    if (file) fd.append('documents', file, partName);
  });
  return fd;
};

// §4 — POST /v1/Crude/AddDetail (multipart). Adds a new assay from the Detail
// page; flips Crude.DataStatus → OnProcess, effective after Review→Approve.
// `payload` = JSON string of AddAssayRequest; `files` = [{ file, partName }].
export const addAssay = ({ payload, files }) =>
  crudeApi.postMultipartRaw(ADD_DETAIL, buildAssayFormData({ payload, files }));

// §5 — PUT /v1/Crude/UpdateDetail (multipart). `payload` = JSON string of
// UpdateAssayRequest; existing docs are kept by sending their `id` in
// payload.documents[] with NO matching file part (only NEW docs get a file).
export const updateAssay = ({ payload, files }) =>
  crudeApi.putMultipartRaw(
    UPDATE_DETAIL,
    buildAssayFormData({ payload, files })
  );

// §7 — GET /v1/Crude/Download/{crudeDocumentId} → binary PDF blob (on the crude
// host). Uses crudeAxios directly with responseType 'blob' (the CrudeAPIClient
// query builder can't set responseType); the response interceptor still unwraps
// `.data`, so this resolves to the Blob itself. Mirrors AuditView's export path.
export const downloadCrudeDocument = docId =>
  crudeAxios.get(`${DOWNLOAD}/${docId}`, { responseType: 'blob' });

// --- Add Assay Draft flow (§AA1/AA2/AA3/AA6) --------------------------------
// Staged Add Assay against an existing crude. Enums accept string OR int here
// (JSON [FromBody]), unlike the single-shot §4 addAssay which requires int.

// AA1 — POST /v1/Crude/AddDetail/Draft (JSON). payload = assay/yield fields +
// crudeId + notes (description/evaluationNote) + masaBerlaku + isLatUpdate; NO
// documents/implementations. Returns { crudeDetailId }. 409 duplicate assayYear,
// 404 if crude missing.
export const createAddDetailDraft = payload =>
  crudeApi.create(ADD_DETAIL_DRAFT, payload);

// AA2 — PUT /v1/Crude/AddDetail/Draft (JSON). Same payload + crudeDetailId.
export const updateAddDetailDraft = payload =>
  crudeApi.put(ADD_DETAIL_DRAFT, payload);

// AA3 — GET /v1/Crude/AddDetail/Draft/{crudeDetailId}. Returns draft fields +
// pendingDocuments[] (docs staged via D4, not yet submitted).
export const getAddDetailDraft = crudeDetailId =>
  crudeApi.get(`${ADD_DETAIL_DRAFT}/${crudeDetailId}`);

// AA6 — POST /v1/Crude/AddDetail/Draft/Submit (JSON): { crudeDetailId,
// implementations }. No masaBerlaku/notes (finalized in AA1). Flips
// Crude.DataStatus → OnProcess. Returns { crudeDetailId }.
export const submitAddDetailDraft = payload =>
  crudeApi.create(ADD_DETAIL_DRAFT_SUBMIT, payload);

// --- Update Assay Draft flow (§UA1/UA2/UA3/UA6) -----------------------------
// Staged Update Assay. UA1 auto-clones the assay's existing documents into
// staging (no file re-upload) — read them back via UA3 to hydrate the doc list.

// UA1 — POST /v1/Crude/UpdateDetail/Draft (JSON). payload = UpdateAssayRequest
// minus documents/implementations, INCLUDING id (= CrudeDetail.Id edited) +
// classificationYieldBefore. Returns { crudeDetailId }. 404 crude/assay missing;
// 409 if another active UPDATE draft already exists for this assay.
export const createUpdateDetailDraft = payload =>
  crudeApi.create(UPDATE_DETAIL_DRAFT, payload);

// UA2 — PUT /v1/Crude/UpdateDetail/Draft (JSON). Same payload + crudeDetailId.
export const updateUpdateDetailDraft = payload =>
  crudeApi.put(UPDATE_DETAIL_DRAFT, payload);

// UA3 — GET /v1/Crude/UpdateDetail/Draft/{crudeDetailId}. Returns draft fields +
// pendingDocuments[] (cloned existing docs AND newly uploaded; no field
// distinguishes them).
export const getUpdateDetailDraft = crudeDetailId =>
  crudeApi.get(`${UPDATE_DETAIL_DRAFT}/${crudeDetailId}`);

// UA6 — POST /v1/Crude/UpdateDetail/Draft/Submit (JSON): { crudeDetailId,
// implementations }. Flips Crude.DataStatus → OnProcess. Returns { crudeDetailId }.
export const submitUpdateDetailDraft = payload =>
  crudeApi.create(UPDATE_DETAIL_DRAFT_SUBMIT, payload);
